
import { useState, useRef, useEffect } from "react";

interface LikertScaleProps {
  question: string;
  onValueChange: (value: number) => void;
  onRelease: () => void;
  currentValue: number;
}

export default function LikertScale({ question, onValueChange, onRelease, currentValue }: LikertScaleProps) {
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const preventScroll = (e: TouchEvent) => {
      if (isDragging) {
        e.preventDefault();
      }
    };

    document.addEventListener('touchmove', preventScroll, { passive: false });
    return () => {
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [isDragging]);

  const getColor = (value: number) => {
    if (value < 20) return "from-red-500 to-red-600";
    if (value < 40) return "from-orange-500 to-orange-600";
    if (value < 60) return "from-yellow-500 to-yellow-600";
    if (value < 80) return "from-lime-500 to-lime-600";
    return "from-green-500 to-green-600";
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    handleTouchMove(e);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!sliderRef.current) return;
    
    const touch = e.touches[0];
    const rect = sliderRef.current.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    
    onValueChange(Math.round(percentage));
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    onRelease();
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    onRelease();
  };

  return (
    <div className="w-full px-6 py-6 bg-white rounded-3xl shadow-xl">
      <h2 className="text-lg font-semibold text-gray-800 mb-6 text-center leading-relaxed">
        {question}
      </h2>
      
      <div className="space-y-3">
        <div 
          ref={sliderRef}
          className="relative pt-2 touch-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <input
            ref={inputRef}
            type="range"
            min="0"
            max="100"
            value={currentValue}
            onChange={(e) => onValueChange(Number(e.target.value))}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            className="w-full h-3 rounded-full appearance-none cursor-pointer opacity-0 relative z-10 pointer-events-none"
            style={{ 
              background: 'transparent'
            }}
          />
          
          <div className="absolute top-2 left-0 right-0 h-3 bg-gradient-to-r from-red-200 via-yellow-200 to-green-200 rounded-full pointer-events-none">
            <div 
              className={`absolute top-0 left-0 h-full bg-gradient-to-r ${getColor(currentValue)} rounded-full transition-all duration-200`}
              style={{ width: `${currentValue}%` }}
            />
          </div>
          
          <div 
            className={`absolute top-0 w-7 h-7 bg-white rounded-full shadow-lg border-4 ${isDragging ? 'scale-125' : 'scale-100'} transition-transform pointer-events-none`}
            style={{ 
              left: `calc(${currentValue}% - 14px)`,
              borderColor: currentValue < 20 ? '#EF4444' : 
                          currentValue < 40 ? '#F97316' : 
                          currentValue < 60 ? '#EAB308' : 
                          currentValue < 80 ? '#84CC16' : '#22C55E'
            }}
          />
        </div>
        
        <div className="flex justify-between text-[10px] text-gray-500 px-1">
          <span className="max-w-[80px] text-left leading-tight">Totalmente en desacuerdo</span>
          <span className="max-w-[80px] text-right leading-tight">Totalmente de acuerdo</span>
        </div>
      </div>
    </div>
  );
}
